import { useEffect, useState, useRef } from 'react';
import classnames from 'classnames';
import { isNil } from 'lodash';
import PropTypes from 'prop-types';
import Skeleton from 'react-loading-skeleton';
import { startOfToday, add, sub, isBefore } from 'date-fns';

import { trackError } from 'utils/helpers';
import {
  AVAILABILITY_BULK,
  addCacheKey,
  isKeyExist,
} from 'utils/helpers/cached_api_parameter_manager';
import EcomEngineAPI from 'api/ecom_engine_api';
import { setBookingFormInitialValues } from 'redux/actions/experience';
import { get7DaysStraight, transferToSeatMap } from './helpers';

const CONSTANT_UNIT = 1;

function AvailabilityQuickView({ resourceId, cardUrl, offCanvasMode }) {
  const [firstDay, setFirstDay] = useState(startOfToday());
  const [dateObj, setDateObj] = useState(get7DaysStraight(firstDay));
  const [seatMap, setSeatMap] = useState({});
  const [loading, setLoading] = useState(false);

  const inputEl = useRef(null);

  const onChangeFirstDay = (date) => {
    setFirstDay(date);
    setDateObj(get7DaysStraight(date));
  };

  useEffect(() => {
    window.ecom.need_wait_react_mounted = false;
    inputEl.current?.addEventListener('change', (e) => {
      const t = e.target.value;
      onChangeFirstDay(new Date(t));
    });
  }, []);

  useEffect(() => {
    if (isNil(resourceId)) {
      return;
    }
    const dateArray = Object.keys(dateObj);
    const fromDate = dateArray[0];
    const toDate = dateArray[dateArray.length - 1];
    const params = {
      resourceIDs: resourceId,
      units: CONSTANT_UNIT,
      fromDate,
      toDate,
    };
    if (isKeyExist(AVAILABILITY_BULK, params)) {
      return;
    }
    setLoading(true);
    EcomEngineAPI.availability
      .dateRangeBulkAvailability(params)
      .then((json) => {
        addCacheKey(AVAILABILITY_BULK, params);
        const newSeatMap = transferToSeatMap(json[resourceId]);
        setSeatMap((v) => ({ ...v, ...newSeatMap }));
      })
      .catch((error) => {
        trackError(error);
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateObj]);

  const onClickNextWeek = () => {
    const nextFirstDay = add(firstDay, { days: 7 });
    onChangeFirstDay(nextFirstDay);
  };

  const onClickPreviousWeek = () => {
    const nextFirstDay = sub(firstDay, { days: 7 });
    const today = startOfToday();
    if (isBefore(nextFirstDay, today)) {
      onChangeFirstDay(today);
    } else {
      onChangeFirstDay(nextFirstDay);
    }
  };

  const setDateTimeInReduxCb = (date) => () => {
    if (window.ecom?.redux_store) {
      window.ecom.redux_store.dispatch(setBookingFormInitialValues({ date }));
    }
  };

  return (
    <>
      <input
        className="d-none availability-quick-view__hidden-input"
        ref={inputEl}
        aria-label="hidden-input"
      />
      <div className="availability-quick-view my-3 d-flex justify-content-between align-items-center text-center">
        <i
          role="button"
          aria-label="previous-week"
          className="p-1 fas fa-chevron-left"
          onClick={onClickPreviousWeek}
        />
        {Object.keys(dateObj).map((key) => {
          const date = dateObj[key];
          const seatNumber = seatMap[key];
          return (
            <div key={key}>
              <div>{date.dayOfMonth}</div>
              {loading || undefined === seatNumber ? (
                <div className="availability-quick-view__skeleton-frame mx-auto">
                  <Skeleton />
                </div>
              ) : (
                <a
                  className={classnames(
                    'd-block availability-quick-view__seat-box mx-auto text-white text-decoration-none',
                    {
                      'bg-success': seatNumber >= 5,
                      'bg-warning': seatNumber < 5 && seatNumber > 0,
                      'bg-danger opacity-75': seatNumber === 0,
                    }
                  )}
                  data-bs-toggle={offCanvasMode ? 'offcanvas' : undefined}
                  href={
                    offCanvasMode
                      ? '#data-booking-drawer'
                      : `${cardUrl}?date=${key}`
                  }
                  onClick={
                    offCanvasMode ? setDateTimeInReduxCb(key) : undefined
                  }
                >
                  {seatNumber}
                </a>
              )}

              <div className="text-uppercase">{date.dayOfWeek}</div>
            </div>
          );
        })}
        <i
          role="button"
          aria-label="next-week"
          className="p-1 fas fa-chevron-right"
          onClick={onClickNextWeek}
        />
      </div>
    </>
  );
}

export default AvailabilityQuickView;

AvailabilityQuickView.propTypes = {
  resourceId: PropTypes.number,
  cardUrl: PropTypes.string,
  offCanvasMode: PropTypes.bool,
};
